import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import UserProfilePopup from './UserProfilePopup';
import NotificationListPopup from './NotificationListPopup';
import UnifiedSearchBar from './UnifiedSearchBar';
import { useAuth } from '../../contexts/AuthContext';

import { getCompanyBranding } from '../../services/companySettingsService';

const Header = ({ userRole = 'admin', userName = 'مستخدم النظام', onQuickMenuToggle, onMenuToggle }) => {
  const navigate = useNavigate();
  const { user, userProfile, signOut } = useAuth();
  const [notificationCount, setNotificationCount] = useState(0);
  const [showUserPopup, setShowUserPopup] = useState(false);
  const [showNotificationPopup, setShowNotificationPopup] = useState(false);
  const [logoUrl, setLogoUrl] = useState('/assets/images/Apropapplogo-1768301171635.jpg');

  const roleLabels = {
    admin: 'مدير النظام',
    manager: 'مدير',
    agent: 'وكيل عقاري',
    staff: 'موظف',
    property_manager: 'مدير الخصائص',
    contract_specialist: 'أخصائي العقود',
    client_manager: 'مدير العملاء',
  };

  useEffect(() => {
    loadCompanyLogo();
  }, []);

  const loadCompanyLogo = async () => {
    try {
      const data = await getCompanyBranding();
      if (data?.logoUrl) {
        setLogoUrl(data?.logoUrl);
      }
    } catch (error) {
      console.error('Error loading company logo:', error);
      // Keep default fallback logo
    }
  };

  const displayName = userProfile?.full_name || userName;
  const displayRole = userProfile?.role ? roleLabels?.[userProfile?.role] : roleLabels?.[userRole];

  // Add this helper function
  const getRoleLabel = (role) => {
    return roleLabels[role] || 'مستخدم';
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/authentication-and-role-assignment');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleNotificationClick = () => {
    setShowNotificationPopup(true);
  };

  const handleProfileClick = () => {
    setShowUserPopup(true);
  };

  const handleQuickActionClick = () => {
    // Open WhatsApp with a default message
    const message = encodeURIComponent('مرحباً، أود الاستفسار عن خدماتكم في إدارة العقارات');
    const whatsappUrl = `https://api.whatsapp.com/send?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  // Callback to update notification count from NotificationListPopup
  const handleNotificationCountChange = (count) => {
    setNotificationCount(count);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black border-b border-white/12">
      <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4">
        {/* Right Section - Logo and Title */}
        <div className="flex items-center gap-4">
          {/* Menu Toggle Button */}
          <button
            onClick={onMenuToggle}
            className="p-2.5 rounded-md hover:bg-white/10 transition-all duration-200 border border-transparent hover:border-white/20"
            aria-label="فتح القائمة"
          >
            <Icon name="Menu" size={24} color="#FFFFFF" />
          </button>

          <button 
            onClick={handleLogoClick}
            className="flex flex-col items-center cursor-pointer hover:opacity-80 transition-opacity duration-200"
            title="العودة إلى الصفحة الرئيسية"
          >
            <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center">
              <img
                src={logoUrl}
                alt="Company Logo"
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.src = '/assets/images/Apropapplogo-1768301171635.jpg';
                }}
              />
            </div>
          </button>
          <div className="hidden md:block">
            <h1 className="text-xl font-heading font-bold text-white mb-0.5">
نظام العمل والمتابعة
            </h1>
            <p className="text-sm text-white/70">
لتسويق وإدارة العقارات
            </p>
          </div>
        </div>

        {/* Center Section - Unified Search Bar */}
        <div className="hidden lg:flex flex-1 justify-center px-6 max-w-3xl mx-auto">
          <UnifiedSearchBar />
        </div>

        {/* Left Section - Actions and User Profile */}
        <div className="flex items-center gap-2 md:gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleQuickActionClick}
            className="hidden md:flex hover:bg-white/10 rounded-md text-white w-10 h-10"
            iconName="MessageSquare"
            iconSize={20}
            title="التواصل عبر واتساب"
          />

          <Button
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            className="text-white/70 hover:text-white hover:bg-white/10"
            title="تسجيل الخروج"
          >
            <Icon name="LogOut" size={20} />
          </Button>

          {/* Notification Bell */}
          <button
            onClick={handleNotificationClick}
            className="relative p-2 hover:bg-gold/10 rounded-lg transition-colors border border-transparent hover:border-gold/30"
            aria-label="الإشعارات"
          >
            <Icon name="Bell" size={20} color="#D4AF37" />
            {notificationCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 border-2 border-black">
                {notificationCount}
              </span>
            )}
          </button>

          {/* User Profile */}
          <button
            onClick={handleProfileClick}
            className="flex items-center gap-2 p-2 hover:bg-gold/10 rounded-lg transition-colors border border-transparent hover:border-gold/30"
            aria-label="القائمة الشخصية"
          >
            <div className="text-right hidden sm:block">
              <p className="text-xs font-semibold text-white leading-tight">{user?.full_name || 'مستخدم'}</p>
              <p className="text-[10px] text-gold">{getRoleLabel(user?.role)}</p>
            </div>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gold to-gold-dark flex items-center justify-center text-black font-bold text-sm border-2 border-gold/40">
              {(user?.full_name || 'م')?.charAt(0)?.toUpperCase()}
            </div>
            <Icon name="ChevronDown" size={16} color="#D4AF37" />
          </button>
        </div>
      </div>

      {/* Popups */}
      <UserProfilePopup 
        userName={displayName}
        userRole={displayRole}
        isOpen={showUserPopup}
        onClose={() => setShowUserPopup(false)}
        userEmail={user?.email}
        onLogout={handleLogout}
      />
      
      <NotificationListPopup 
        isOpen={showNotificationPopup}
        onClose={() => setShowNotificationPopup(false)}
        onUnreadCountChange={handleNotificationCountChange}
      />
    </header>
  );
};

export default Header;